import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Building2,
  DollarSign,
  Calendar,
  GitBranch,
  Mail,
  Phone,
  LayoutGrid,
} from "lucide-react";
import { useGlobalSearch, SearchResult } from "@/hooks/app/useGlobalSearch";
import { Highlighted } from "./Highlighted";
import { cn } from "@/lib/utils";

interface InlineSearchDropdownProps {
  className?: string;
}

const getTypeIcon = (type: SearchResult['type']) => {
  switch (type) {
    case 'lead':
      return User;
    case 'company':
      return Building2;
    case 'deal':
      return DollarSign;
    case 'activity':
      return Calendar;
    case 'sequence':
      return GitBranch;
    default:
      return Search;
  }
};

const getTypeLabel = (type: SearchResult['type']) => {
  switch (type) {
    case 'lead':
      return 'Leads';
    case 'company':
      return 'Empresas';
    case 'deal':
      return 'Negócios';
    case 'activity':
      return 'Atividades';
    case 'sequence':
      return 'Cadências';
    default:
      return 'Resultados';
  }
};

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'accent' | 'orange';

const getStatusColor = (status: string | undefined, type: SearchResult['type']): BadgeVariant => {
  if (!status) return 'secondary';

  const s = status.toLowerCase();

  // Estados universais — vencem por significado, independente do tipo
  if (['done', 'completed', 'finished'].includes(s)) {
    return 'info';                                  // azul — concluído/finalizado
  }
  if (['won', 'active', 'enabled', 'success'].includes(s)) {
    return 'success';                               // verde — sucesso/ativo
  }
  if (['lost', 'cancelled', 'canceled', 'failed', 'error', 'archived'].includes(s)) {
    return 'destructive';                           // vermelho — falha/cancelado
  }
  if (['inactive', 'disabled', 'idle'].includes(s)) {
    return 'secondary';                             // cinza — desligado/neutro
  }
  if (['pending', 'in_progress', 'in-progress'].includes(s)) {
    return 'orange';                                // laranja — aguardando ação
  }
  if (['contacted', 'paused', 'prospect', 'warning'].includes(s)) {
    return 'warning';                               // amarelo — em progresso/potencial
  }
  if (s === 'new') {
    return 'accent';                                // ciano — recém-criado
  }
  if (s === 'draft') {
    return 'secondary';
  }

  // Estados específicos por tipo
  switch (type) {
    case 'lead':
      if (s === 'qualified') return 'default';     // roxo — destaque de conversão
      return 'outline';
    case 'deal':
    case 'activity':
    case 'sequence':
    case 'company':
    default:
      return 'secondary';
  }
};

// Rótulos PT-BR dos status (reproduz os mapas espalhados em Leads/LeadProfile/
// Activities/CompanyEdit). Alguns variam por tipo (lost, active, inactive).
const STATUS_LABELS: Record<string, string> = {
  new: 'Novo',
  contacted: 'Em contato',
  qualified: 'Qualificado',
  meeting: 'Reunião',
  proposal: 'Proposta',
  won: 'Ganho',
  open: 'Aberto',
  deleted: 'Excluído',
  pending: 'Pendente',
  done: 'Concluída',
  completed: 'Concluída',
  canceled: 'Cancelada',
  cancelled: 'Cancelada',
  in_progress: 'Em andamento',
  'in-progress': 'Em andamento',
  prospect: 'Prospect',
  customer: 'Cliente',
  churned: 'Inativo',
  draft: 'Rascunho',
};

const getStatusLabel = (status: string | undefined, type: SearchResult['type']): string | undefined => {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (s === 'lost') return type === 'deal' ? 'Perdido' : 'Descartado';
  if (s === 'active') return type === 'sequence' ? 'Ativa' : 'Ativo';
  if (s === 'inactive') return type === 'sequence' ? 'Inativa' : 'Inativo';
  return STATUS_LABELS[s] ?? status;
};

// Ordem fixa das categorias na sidebar.
const TYPE_ORDER: SearchResult['type'][] = ['deal', 'lead', 'company', 'activity', 'sequence'];

// Ícone para a linha "onde casou" — só email/telefone ganham ícone (como antes).
const getFieldIcon = (label: string | undefined) => {
  if (label === 'Email' || label === 'Email do lead') return Mail;
  if (label === 'Telefone' || label === 'Telefone do lead') return Phone;
  return null;
};

export const InlineSearchDropdown = ({ className }: InlineSearchDropdownProps) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [activeCategory, setActiveCategory] = useState<'all' | SearchResult['type']>('all');
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const { data: results = [], isLoading } = useGlobalSearch(debouncedQuery);

  // Debounce search query
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when there's a query and input is focused
  useEffect(() => {
    setIsOpen(query.length > 0 && document.activeElement === inputRef.current);
  }, [query]);

  // Nova query: volta para "todas" e zera o foco.
  useEffect(() => {
    setActiveCategory('all');
    setFocusedIndex(-1);
  }, [debouncedQuery]);

  // Troca de categoria: zera o foco (índice é relativo à lista filtrada).
  useEffect(() => {
    setFocusedIndex(-1);
  }, [activeCategory]);

  // Contagem por tipo + categorias presentes nos resultados.
  const countsByType = useMemo(() => {
    const counts: Record<SearchResult['type'], number> = {
      deal: 0, lead: 0, company: 0, activity: 0, sequence: 0,
    };
    for (const r of results) counts[r.type] += 1;
    return counts;
  }, [results]);

  const presentTypes = useMemo(
    () => TYPE_ORDER.filter((t) => countsByType[t] > 0),
    [countsByType],
  );

  const filteredResults = useMemo(
    () => (activeCategory === 'all' ? results : results.filter((r) => r.type === activeCategory)),
    [results, activeCategory],
  );

  // Mantém o item focado pelo teclado visível na lista rolável.
  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-result-index="${focusedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const handleSelect = (result: SearchResult) => {
    setQuery("");
    setIsOpen(false);
    setFocusedIndex(-1);
    navigate(result.href);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
      inputRef.current?.blur();
      return;
    }
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredResults[focusedIndex]) {
          handleSelect(filteredResults[focusedIndex]);
        }
        break;
    }
  };

  const hasResults = results.length > 0;

  return (
    <div ref={containerRef} className={cn("relative w-full max-w-md", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          data-navbar-search
          placeholder="Buscar por nome, telefone, email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          className="h-9 min-h-9 rounded-full border-0 bg-white py-0 pl-4 pr-10 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <button
          type="button"
          aria-label="Buscar"
          tabIndex={-1}
          onClick={() => inputRef.current?.focus()}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-hover"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Overlay de resultados (2 painéis) */}
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[640px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg animate-in fade-in-0 slide-in-from-top-2 zoom-in-95 duration-150">
          {isLoading && query && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-2 text-popover-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span>Buscando...</span>
              </div>
            </div>
          )}

          {!isLoading && query && !hasResults && (
            <div className="px-4 py-8 text-center text-popover-foreground/70">
              Nenhum resultado encontrado.
            </div>
          )}

          {!isLoading && hasResults && (
            <div className="flex">
              {/* Sidebar de categorias */}
              <aside className="w-[180px] shrink-0 overflow-y-auto border-r bg-muted/30 py-1.5">
                <CategoryItem
                  active={activeCategory === 'all'}
                  Icon={LayoutGrid}
                  label="Todas as categorias"
                  count={results.length}
                  onClick={() => setActiveCategory('all')}
                />
                <div className="mx-3 my-1 h-px bg-border/60" />
                {presentTypes.map((type) => (
                  <CategoryItem
                    key={type}
                    active={activeCategory === type}
                    Icon={getTypeIcon(type)}
                    label={getTypeLabel(type)}
                    count={countsByType[type]}
                    onClick={() => setActiveCategory(type)}
                  />
                ))}
              </aside>

              {/* Painel de resultados */}
              <div
                ref={listRef}
                key={activeCategory}
                className="max-h-96 min-w-0 flex-1 overflow-y-auto py-1 animate-in fade-in-0 duration-150"
              >
                {filteredResults.map((result, index) => {
                  const Icon = getTypeIcon(result.type);
                  const typeLabel = getTypeLabel(result.type).replace(/s$/, ''); // singular
                  const FieldIcon = getFieldIcon(result.matchedFieldLabel);
                  const RpcFieldIcon = getFieldIcon(result.matchedField);
                  const hasRpcMatch =
                    result.matchedField &&
                    result.matchedValue &&
                    result.matchedField !== "Nome" &&
                    result.matchedField !== "Título";

                  return (
                    <Link
                      key={`${result.type}-${result.id}`}
                      to={result.href}
                      data-result-index={index}
                      className={cn(
                        "flex items-start gap-3 border-b border-border px-4 py-3 text-inherit no-underline transition-colors last:border-b-0 hover:bg-muted",
                        focusedIndex === index && "bg-muted/60",
                      )}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
                          return;
                        }
                        setQuery("");
                        setIsOpen(false);
                        setFocusedIndex(-1);
                      }}
                    >
                      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-popover-foreground/70" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Highlighted
                            text={result.highlightedTitle ?? result.title}
                            className="min-w-0 truncate font-medium text-popover-foreground"
                          />
                          <span className="flex-shrink-0 text-[10px] uppercase tracking-wider text-popover-foreground/50">
                            {typeLabel}
                          </span>
                        </div>

                        {result.matchedSnippet && result.matchedFieldLabel ? (
                          <div className="mt-0.5 line-clamp-2 text-sm text-popover-foreground/70">
                            {FieldIcon && (
                              <FieldIcon className="mr-1 inline-block h-3 w-3 align-text-bottom text-popover-foreground/50" />
                            )}
                            <span className="text-popover-foreground/50">{result.matchedFieldLabel}: </span>
                            <Highlighted text={result.matchedSnippet} />
                          </div>
                        ) : hasRpcMatch ? (
                          <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-popover-foreground/70">
                            {RpcFieldIcon && <RpcFieldIcon className="h-3 w-3 flex-shrink-0" />}
                            <span className="text-popover-foreground/50">{result.matchedField}:</span>
                            <span className="truncate">{result.matchedValue}</span>
                          </div>
                        ) : result.subtitle ? (
                          <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-popover-foreground/70">
                            {result.type === 'deal' && (
                              <GitBranch className="h-3 w-3 flex-shrink-0 text-popover-foreground/50" />
                            )}
                            <span className="truncate">{result.subtitle}</span>
                          </div>
                        ) : null}
                      </div>

                      {((result.type === 'deal' && result.amount != null) || result.status) && (
                        <div className="flex flex-shrink-0 flex-col items-end gap-1">
                          {result.type === 'deal' && result.amount != null && (
                            <span className="whitespace-nowrap text-sm font-semibold text-popover-foreground">
                              R$ {result.amount.toLocaleString('pt-BR')}
                            </span>
                          )}
                          {result.status && (
                            <Badge
                              variant={getStatusColor(result.status, result.type)}
                              className="text-xs"
                            >
                              {getStatusLabel(result.status, result.type)}
                            </Badge>
                          )}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface CategoryItemProps {
  active: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  onClick: () => void;
}

const CategoryItem = ({ active, Icon, label, count, onClick }: CategoryItemProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left text-sm transition-all duration-150",
      active
        ? "border-primary bg-primary/10 font-semibold text-primary"
        : "border-transparent text-popover-foreground/70 hover:bg-background/60",
    )}
  >
    <Icon className={cn("h-4 w-4 flex-shrink-0", active ? "text-primary" : "text-popover-foreground/60")} />
    <span className="min-w-0 flex-1 truncate">{label}</span>
    <span className={cn("flex-shrink-0 text-xs", active ? "text-primary" : "text-popover-foreground/50")}>{count}</span>
  </button>
);
